import {startOfToday} from 'date-fns';
import {closeCurrentDialog, Form, FormField, showNotification} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {usePatchCoreExchangeRmaDenialMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {getApiDateTimeString, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DialogActions} from './DialogActions';

const RmaDenialFormSchema = z.object({
  rmaResponseDate: z.date(),
  rmaNote: z.string(),
});

type RmaDenialForm = z.infer<typeof RmaDenialFormSchema>;

interface RmaDenialDialogProps extends RequiredTestIdProps {
  coreExchangeId: string;
  onSaved: VoidFunction;
}

export function RmaDenialDialog(props: RmaDenialDialogProps) {
  const [patchRmaDenial, {isLoading}] = usePatchCoreExchangeRmaDenialMutation();

  const handleSubmit = async (values: RmaDenialForm) => {
    await patchRmaDenial({
      coreExchangeId: props.coreExchangeId,
      body: {
        rmaResponseDate: getApiDateTimeString(values.rmaResponseDate),
        rmaNote: values.rmaNote,
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyUpdated'));
        props.onSaved();
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form<RmaDenialForm>
      experimentalZodSchema={RmaDenialFormSchema}
      onSubmit={handleSubmit}
      defaultValues={{rmaResponseDate: startOfToday(), rmaNote: ''}}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            type="date"
            name="rmaResponseDate"
            label={i18n.t('entity.warehouse.labels.coreExchangeRmaResponseDate')}
            isRequired
            data-testid={suffixTestId('inputs.rmaResponseDate', props)}
          />
          <FormField
            control={control}
            type="textarea"
            name="rmaNote"
            label={i18n.t('general.labels.reason')}
            data-testid={suffixTestId('inputs.rmaNote', props)}
          />
          <DialogActions
            control={control}
            isLoading={isLoading}
            data-testid={props['data-testid']}
          />
        </VStack>
      )}
    </Form>
  );
}
